import { useState } from 'react';
import { getBaseUrlForFetching } from '../../App';

interface UseHandleAudio2TextReturn {
  isConverting: boolean;
  convertAudioToText: (audioBlob: Blob) => Promise<string>;
  error: string | null;
}

// Maximum number of retry attempts for API calls
const MAX_RETRIES = 2;
// Delay between retries in milliseconds
const RETRY_DELAY = 1000;

const useHandleAudio2Text = (): UseHandleAudio2TextReturn => {
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to delay execution
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Helper function to validate audio blob before sending
  const validateAudioBlob = (blob: Blob): boolean => {
    // Check if blob has reasonable size
    if (blob.size < 100) {
      console.warn('Audio blob is too small, may not contain valid audio');
      return false;
    }
    
    // Check if blob has valid type
    const validTypes = ['audio/webm', 'audio/ogg', 'audio/mp4', 'audio/mpeg', 'audio/wav'];
    const hasValidType = validTypes.some(type => blob.type.includes(type));
    
    if (!hasValidType) {
      console.warn(`Audio blob has potentially unsupported type: ${blob.type}`);
      // Still return true as some browsers might use custom MIME types
      return true;
    }
    
    return true;
  };

  const convertAudioToText = async (audioBlob: Blob): Promise<string> => {
    setIsConverting(true);
    setError(null);

    // Validate the audio blob
    if (!validateAudioBlob(audioBlob)) {
      setError('Invalid audio data');
      setIsConverting(false);
      return '';
    }

    try {
      console.log(`Converting audio blob: size=${audioBlob.size} bytes, type=${audioBlob.type}`);
      
      // Convert blob to base64
      const base64Audio = await blobToBase64(audioBlob);
      console.log(`Audio converted to base64, length: ${base64Audio.length} characters`);
      
      const baseUrl = getBaseUrlForFetching();
      console.log(`Sending audio to: ${baseUrl}/audio2Text`);
      
      // Try multiple times with exponential backoff
      let lastError = null;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
          console.log(`Retry attempt ${attempt}/${MAX_RETRIES} after ${RETRY_DELAY * attempt}ms`);
          await delay(RETRY_DELAY * attempt);
        }
        
        try {
          const response = await fetch(`${baseUrl}/audio2Text`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              audioFile: base64Audio,
              format: audioBlob.type,
              // Add additional diagnostic info
              diagnosticInfo: {
                blobSize: audioBlob.size,
                blobType: audioBlob.type,
                timestamp: new Date().toISOString(),
                clientInfo: navigator.userAgent
              }
            }),
          });

          console.log(`Response status: ${response.status}`);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Server error response (attempt ${attempt + 1}):`, errorText);
            lastError = new Error(`Server responded with status: ${response.status}, ${errorText}`);
            // Continue to next retry attempt
            continue;
          }

          const data = await response.json();
          console.log('Transcription data received:', data);
          
          if (!data.transcription) {
            console.warn('Server returned empty transcription');
            // If we got a response but no transcription, try client-side fallback
            if (attempt === MAX_RETRIES && 'webkitSpeechRecognition' in window) {
              console.log('Attempting client-side speech recognition fallback');
              // This is just a placeholder - actual implementation would depend on browser support
              return 'Speech recognition not fully implemented';
            }
            continue;
          }
          
          return data.transcription;
        } catch (err) {
          console.error(`API call error (attempt ${attempt + 1}):`, err);
          lastError = err;
          // Continue to next retry attempt
        }
      }
      
      // If we get here, all attempts failed
      throw lastError || new Error('Failed to convert audio after multiple attempts');
    } catch (err) {
      console.error('Error converting audio to text:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to convert audio to text';
      setError(errorMessage);
      return '';
    } finally {
      setIsConverting(false);
    }
  };

  return {
    isConverting,
    convertAudioToText,
    error
  };
};

// Helper function to convert Blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Extract the base64 data part (remove the data URL prefix)
      const base64Data = base64String.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => {
      console.error('Error reading blob as base64:', error);
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

export default useHandleAudio2Text; 